import * as React from 'react';
import { Column, Row } from '@narvar/nth-grid';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import A11yDiv from '@narvar/nth-a11y-div';
import TextLink, { TextLinkTargetType } from '@narvar/nth-bit-text-link';
import Button, { ButtonType } from '@narvar/nth-block-button';
import { MediumHeader } from '@narvar/nth-bit-header';
import { LocationDetail } from '../../../../server/api/schema';
import LocationHourItem, {
  LocationHourItemTranslations,
} from '../LocationHourItem';
import {
  ActionsRow,
  DistanceWrapper,
  AddressRow,
  Container,
  NameWrapper,
  VisibleDivSmall,
  PhoneWrapper,
  ButtonWrapper,
  CollapsedRow,
  LogoWrapper,
} from './components';
import { sanitizePhoneString } from '../../../lib/phone';
import { getGoogleMapsDirectionsLink } from '../../../lib/links';
import LocationLogo from '../LocationLogo';

interface LocationItemProps {
  selected: boolean;
  location: LocationDetail;
  handleSelectItem: () => void;
  handleSelectLocation: () => void;
  selectLocationText: string;
  translations: LocationHourItemTranslations;
}

const LocationItem: React.FunctionComponent<LocationItemProps> = ({
  location,
  selected,
  handleSelectItem,
  handleSelectLocation,
  selectLocationText,
  translations,
}: LocationItemProps) => {
  const {
    bodytextDefaultNone,
    captiontextDefaultNone,
    linksTrackingDefault,
    linksTrackingHover,
    panelcontainerNoneNone,
    panelcontainerNoneActive,
  } = useTheme();

  const hasPhone = location.phone !== undefined;
  const addressLine1 = `${location.address.street1}${
    location.address.street2 ? ', ' : ''
  }${location.address.street2 || ''}`;
  const addressLine2 = `${location.address.city}, ${location.address.stateOrProvince} ${location.address.postalCode}`;

  const distance =
    typeof location.distance === 'string'
      ? parseFloat(location.distance)
      : location.distance;

  return (
    <A11yDiv onClick={selected ? null : handleSelectItem}>
      <Container
        s={
          // Default to base container styling if an active is not defined
          selected
            ? panelcontainerNoneActive ?? panelcontainerNoneNone
            : panelcontainerNoneNone
        }
      >
        <CollapsedRow>
          <Column xs-columns={3} sm-columns={2}>
            <LogoWrapper>
              <LocationLogo
                s={{ width: '100%' }}
                providerId={location.providerId}
              />
            </LogoWrapper>
          </Column>
          <Column xs-columns={9} sm-columns={10}>
            <AddressRow>
              <NameWrapper xs-columns={12} sm-columns={5}>
                <MediumHeader>{location.name}</MediumHeader>
                {location.additionalDescription !== undefined && (
                  <div style={bodytextDefaultNone}>
                    {location.additionalDescription}
                  </div>
                )}
              </NameWrapper>
              <Column xs-columns={12} sm-columns={5}>
                {selected ? (
                  <TextLink
                    linkStyle={{
                      sDefault: linksTrackingDefault,
                      sHover: linksTrackingHover,
                    }}
                    href={getGoogleMapsDirectionsLink({
                      destination: `${addressLine1}, ${addressLine2}`,
                    })}
                    target={TextLinkTargetType.BLANK}
                  >
                    <div>{addressLine1}</div>
                    <div>{addressLine2}</div>
                  </TextLink>
                ) : (
                  <>
                    <div style={bodytextDefaultNone}>{addressLine1}</div>
                    <VisibleDivSmall style={bodytextDefaultNone}>
                      {addressLine2}
                    </VisibleDivSmall>
                  </>
                )}
              </Column>
              <DistanceWrapper xs-columns={12} sm-columns={2}>
                <div style={captiontextDefaultNone}>
                  {`${distance ? distance.toFixed(1) : ''} ${
                    location.distanceUnit?.toLowerCase() || ''
                  }`}
                </div>
              </DistanceWrapper>
            </AddressRow>
          </Column>
        </CollapsedRow>
        {selected && (
          <div style={{ marginTop: '1rem' }}>
            <Row>
              <Column xs-columns={12} sm-columns={4}>
                {location.hours &&
                  location.hours.map((h) => (
                    <LocationHourItem
                      key={h.dayOfWeek}
                      hourData={h}
                      translations={translations}
                      featureName="redirectToHold"
                    />
                  ))}
              </Column>
            </Row>
            <ActionsRow>
              {hasPhone && (
                <PhoneWrapper xs-columns={12} sm-columns={4}>
                  <TextLink
                    linkStyle={{
                      sDefault: linksTrackingDefault,
                      sHover: linksTrackingHover,
                    }}
                    href={`tel:${sanitizePhoneString(location.phone ?? '')}`}
                  >
                    {location.phone}
                  </TextLink>
                </PhoneWrapper>
              )}
              <ButtonWrapper
                xs-columns={12}
                sm-columns={4}
                sm-column-offset={hasPhone ? 4 : 8}
              >
                <Button
                  big
                  type={ButtonType.submit}
                  disabled={false}
                  onClick={handleSelectLocation}
                >
                  {selectLocationText}
                </Button>
              </ButtonWrapper>
            </ActionsRow>
          </div>
        )}
      </Container>
    </A11yDiv>
  );
};

export default withThemeProvider(LocationItem, {
  themeTokens: [
    'bodytextDefaultNone',
    'captiontextDefaultNone',
    'linksTrackingDefault',
    'linksTrackingHover',
    'panelcontainerNoneNone',
    'panelcontainerNoneActive',
  ],
});
